import React, { useEffect, useRef, useState } from 'react';
import { Check, CheckCheck, Clock, AlertCircle, Paperclip, RotateCcw, X, MoreHorizontal, Heart, Ban, MousePointerClick } from 'lucide-react';
import { MessageContextMenu } from '@/components/whatsapp2/messages/MessageContextMenu';
import { tip } from '@/components/messages/ChatTooltip';
import { supabase } from '@/lib/supabase';
import type { MetaMessage, MetaAttachment } from '@/lib/stores/metaMessagingStore';
import { messageTimeLabel } from './metaConversationHelpers';
import { HEART, canQuoteOrReact, highlightParts, metaAttachmentLabel, metaAttachmentUnavailableLabel, onlyHeartReaction, specialNotice, type MetaChannel } from './metaMessagingRules';

interface MetaMessageBubbleProps {
  message: MetaMessage;
  onRetry: () => void;
  onDiscard: () => void;
  /** Messaggio citato (risposta a un messaggio preciso), gia' trovato nel thread. */
  quoted?: MetaMessage | null;
  /** Assenti se l'utente non puo' rispondere: niente menu azioni. */
  onReply?: () => void;
  onReact?: (reaction: string | null) => void;
  /** Canale: su Instagram l'unica reazione possibile e' il cuore. */
  channel: MetaChannel;
  /** Testo cercato nel thread: evidenziato nella bolla. */
  searchQuery?: string;
  /** Risultato della ricerca selezionato ora: bordo in evidenza. */
  activeMatch?: boolean;
}

/** Testo breve per citazioni e anteprime: il testo o il tipo di allegato. */
export function metaMessageSummary(m: MetaMessage | null | undefined): string {
  if (!m) return 'Messaggio precedente';
  if (m.deleted_at) return 'Messaggio eliminato';
  if (m.content) return m.content;
  const t = m.attachments?.[0]?.type;
  return t ? metaAttachmentLabel(t) : 'Messaggio';
}

const META_MEDIA_BUCKET = 'meta-media';
const SIGNED_URL_TTL = 60 * 60; // 1 ora
const signedUrlCache = new Map<string, { url: string; expires: number }>();

/** URL da mostrare: copia nello storage (firmata, 1 ora) se c'e', altrimenti l'URL Meta finche' vale. */
function useAttachmentUrl(att: MetaAttachment): { url: string | null; loading: boolean } {
  const [url, setUrl] = useState<string | null>(att.storage_path ? null : att.url);
  const [loading, setLoading] = useState<boolean>(Boolean(att.storage_path));

  useEffect(() => {
    let cancelled = false;
    if (!att.storage_path) {
      setUrl(att.url);
      setLoading(false);
      return;
    }
    const cached = signedUrlCache.get(att.storage_path);
    if (cached && cached.expires > Date.now() + 60_000) {
      setUrl(cached.url);
      setLoading(false);
      return;
    }
    setLoading(true);
    supabase.storage
      .from(META_MEDIA_BUCKET)
      .createSignedUrl(att.storage_path, SIGNED_URL_TTL)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data?.signedUrl) {
          setUrl(att.url);
        } else {
          signedUrlCache.set(att.storage_path as string, { url: data.signedUrl, expires: Date.now() + SIGNED_URL_TTL * 1000 });
          setUrl(data.signedUrl);
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [att.storage_path, att.url]);

  return { url, loading };
}

function AttachmentView({ att, mine }: { att: MetaAttachment; mine: boolean }) {
  const { url, loading } = useAttachmentUrl(att);
  const [broken, setBroken] = useState(false);
  const label = metaAttachmentLabel(att.type);
  const mime = att.mime_type ?? '';
  const isImage = att.type === 'image' || mime.startsWith('image/');
  const isVideo = att.type === 'video' || att.type === 'ig_reel' || att.type === 'reel' || mime.startsWith('video/');
  const isAudio = att.type === 'audio' || mime.startsWith('audio/');
  const muted = mine ? 'text-white/90' : 'text-[#787774]';

  if (loading) return <div className="mt-1 h-24 w-40 rounded-lg bg-black/10 animate-pulse" aria-label={label} />;

  if (!url || broken) {
    return (
      <div className={`mt-1 text-xs inline-flex items-center gap-1 ${muted}`}>
        <Paperclip className="h-3 w-3" />
        {metaAttachmentUnavailableLabel(att.type)}
      </div>
    );
  }
  if (isImage) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block mt-1">
        <img
          src={url}
          alt={label}
          loading="lazy"
          onError={() => setBroken(true)}
          className="max-h-64 max-w-full rounded-lg object-cover animate-fade-in"
        />
      </a>
    );
  }
  if (isVideo) {
    return <video src={url} controls preload="metadata" onError={() => setBroken(true)} className="mt-1 max-h-64 max-w-full rounded-lg" />;
  }
  if (isAudio) {
    return <audio src={url} controls preload="metadata" onError={() => setBroken(true)} className="mt-1 max-w-full" />;
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className={`mt-1 text-xs inline-flex items-center gap-1 underline ${muted}`}>
      <Paperclip className="h-3 w-3" />
      {label}
    </a>
  );
}

/** Anteprima della storia citata: l'URL Meta scade, quando non carica resta la scritta. */
function StoryPreview({ url }: { url: string }) {
  const [broken, setBroken] = useState(false);
  // La storia puo' essere un video (l'immagine non carica) o scaduta: resta il link.
  if (broken) return <a href={url} target="_blank" rel="noreferrer" className="underline opacity-80">Apri la storia (se ancora disponibile)</a>;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="block mt-1">
      <img src={url} alt="Storia citata" loading="lazy" onError={() => setBroken(true)} className="h-20 w-auto max-w-[8rem] rounded object-cover" />
    </a>
  );
}

function StatusTicks({ message }: { message: MetaMessage }) {
  if (message.status === 'sending') return <Clock className="h-3 w-3" aria-label="In invio" {...tip('In invio')} />;
  if (message.status === 'failed') return <AlertCircle className="h-3 w-3 text-red-500" aria-label="Non inviato" />;
  if (message.read_at) return <CheckCheck className="h-3.5 w-3.5 text-[#53bdeb]" aria-label="Letto" {...tip('Letto dal contatto')} />;
  if (message.delivered_at) return <CheckCheck className="h-3.5 w-3.5" aria-label="Consegnato" {...tip('Consegnato')} />;
  return <Check className="h-3.5 w-3.5" aria-label="Inviato" {...tip('Inviato')} />;
}

/**
 * Bolla di un messaggio Instagram/Messenger, con lo stesso aspetto delle chat WhatsApp
 * (verde i nostri, bianco i loro): testo, allegati in linea, citazione del messaggio a
 * cui risponde, reazione messa dal CRM, orario e spunte; bolla ottimistica e fallita con
 * Riprova / Scarta; menu "..." con Rispondi e reazioni.
 */
export function MetaMessageBubble({ message, onRetry, onDiscard, quoted, onReply, onReact, channel, searchQuery, activeMatch }: MetaMessageBubbleProps) {
  const mine = message.direction === 'outbound';
  const failed = message.status === 'failed';
  const [showMenu, setShowMenu] = useState(false);
  const menuAnchorRef = useRef<HTMLButtonElement>(null);
  // Rispondi / Reagisci solo con il mid Meta (senza, Meta non saprebbe a cosa si riferiscono).
  const canAct = !!onReply && canQuoteOrReact(message);
  const heartOnly = onlyHeartReaction(channel);
  const notice = specialNotice(message, channel);
  const storyReply = message.story_reply && (message.story_reply.url || message.story_reply.id) ? message.story_reply : null;

  const trigger = canAct ? (
    <button
      ref={menuAnchorRef}
      type="button"
      onClick={(e) => { e.stopPropagation(); setShowMenu((v) => !v); }}
      className={`${showMenu ? 'opacity-100 bg-[rgba(55,53,47,0.08)]' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'} self-center p-1 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 transition-opacity text-[#787774]`}
      aria-label="Azioni sul messaggio"
      aria-haspopup="menu"
      aria-expanded={showMenu}
      {...tip(mine || heartOnly ? 'Rispondi citando questo messaggio' : 'Rispondi o reagisci')}
    >
      <MoreHorizontal className="h-4 w-4" />
    </button>
  ) : null;

  // Instagram: al posto della riga di emoji, un solo pulsante cuore sui messaggi del contatto.
  const heartButton =
    canAct && heartOnly && !mine && onReact ? (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onReact(message.our_reaction ? null : HEART); }}
        className={`${message.our_reaction ? 'opacity-100 text-red-500' : 'opacity-0 group-hover:opacity-100 focus:opacity-100 text-[#787774]'} self-center p-1 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 transition-opacity`}
        aria-label={message.our_reaction ? 'Togli il cuore' : 'Metti un cuore'}
        {...tip(message.our_reaction ? 'Togli il cuore' : 'Metti un cuore (Instagram accetta solo questa reazione)')}
      >
        <Heart className={`h-4 w-4 ${message.our_reaction ? 'fill-current' : ''}`} />
      </button>
    ) : null;

  const renderText = (text: string) =>
    searchQuery
      ? highlightParts(text, searchQuery).map((p, i) =>
          p.match ? <mark key={i} className="bg-yellow-200 dark:bg-yellow-600/60 text-inherit rounded-sm">{p.text}</mark> : <React.Fragment key={i}>{p.text}</React.Fragment>
        )
      : text;

  return (
    <div className={`group flex items-end gap-1 ${mine ? 'justify-end' : 'justify-start'} animate-fade-in`}>
      {mine && trigger}
      <div className="relative max-w-[75%]">
        <div
          className={[
            'rounded-lg px-2.5 py-1.5 text-sm whitespace-pre-wrap break-words shadow-sm transition-opacity',
            activeMatch ? 'ring-2 ring-[#2383e2]' : '',
            mine
              ? `${failed ? 'bg-red-50 border border-red-200 dark:bg-red-900/30 dark:border-red-800' : 'bg-[#D9FDD3] dark:bg-[#005C4B]'} text-[#111B21] dark:text-[#E9EDEF] rounded-br-sm ${message.status === 'sending' ? 'opacity-70' : ''}`
              : 'bg-white dark:bg-gray-800 text-[#37352F] dark:text-white rounded-bl-sm',
          ].join(' ')}
        >
          {message.reply_to_mid && (
            <div className={`mb-1 px-2 py-1 rounded border-l-4 text-xs ${mine ? 'bg-black/5 border-[#06cf9c]' : 'bg-[rgba(55,53,47,0.05)] border-[#2383e2]'}`}>
              <div className="font-medium text-[11px] opacity-80">{quoted ? (quoted.direction === 'outbound' ? 'Tu' : 'Contatto') : 'Risposta a'}</div>
              <div className="line-clamp-2 opacity-80">{metaMessageSummary(quoted)}</div>
            </div>
          )}
          {storyReply && (
            <div className="mb-1 px-2 py-1 rounded border-l-4 text-xs bg-[rgba(55,53,47,0.05)] border-[#c13584]">
              <div className="font-medium text-[11px] opacity-80">Ha risposto alla tua storia</div>
              {storyReply.url && /^https:\/\//i.test(storyReply.url) ? <StoryPreview url={storyReply.url} /> : <div className="opacity-70">Storia non più disponibile</div>}
            </div>
          )}
          {message.postback_payload !== undefined && message.postback_payload !== null && (
            <div className="mb-0.5 inline-flex items-center gap-1 text-[10px] text-[#787774]" {...tip('Il contatto ha premuto un pulsante o una domanda rapida')}>
              <MousePointerClick className="h-3 w-3" /> Pulsante premuto
            </div>
          )}
          {notice ? (
            <div className="inline-flex items-center gap-1 italic text-[#787774] dark:text-white/60">
              <Ban className="h-3.5 w-3.5" /> {notice}
            </div>
          ) : (
            <>
              {message.content && <div>{renderText(message.content)}</div>}
              {message.attachments?.map((a, i) => (
                <AttachmentView key={i} att={a} mine={false} />
              ))}
            </>
          )}
          <div className="mt-0.5 text-[10px] flex items-center gap-1 justify-end text-[#667781] dark:text-white/60">
            {message.edited_at && !message.deleted_at && (
              <span className="italic" {...tip(`Modificato dal contatto il ${messageTimeLabel(message.edited_at)}`)}>modificato</span>
            )}
            <span>{messageTimeLabel(message.sent_at)}</span>
            {mine && <StatusTicks message={message} />}
          </div>
          {failed && (
            <div className="mt-1.5 flex items-center gap-2 text-[11px] text-red-700 dark:text-red-300">
              <span>{message.error ?? 'Non inviato'}</span>
              <button type="button" onClick={onRetry} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-100 hover:bg-red-200 dark:bg-red-900/40">
                <RotateCcw className="h-3 w-3" /> Riprova
              </button>
              <button type="button" onClick={onDiscard} aria-label="Scarta" className="p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/40" {...tip('Scarta il messaggio non inviato')}>
                <X className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
        {message.contact_reaction && (
          <span
            className={`absolute -bottom-3 ${mine ? 'left-2' : 'right-2'} text-sm leading-none bg-white dark:bg-gray-700 border border-[rgba(55,53,47,0.09)] rounded-full px-1 py-0.5 shadow-sm`}
            role="img"
            aria-label={`Reazione del contatto: ${message.contact_reaction}`}
            {...tip('Reazione del contatto')}
          >
            {message.contact_reaction}
          </span>
        )}
        {message.our_reaction && (
          <button
            type="button"
            onClick={() => onReact?.(null)}
            className={`absolute -bottom-3 ${mine ? 'right-2' : 'left-2'} text-sm leading-none bg-white dark:bg-gray-700 border border-[rgba(55,53,47,0.09)] rounded-full px-1 py-0.5 shadow-sm`}
            {...tip(onReact ? 'La tua reazione: clicca per toglierla' : 'La tua reazione')}
          >
            {message.our_reaction}
          </button>
        )}
      </div>
      {!mine && heartButton}
      {!mine && trigger}
      {showMenu && canAct && (
        <MessageContextMenu
          messageId={message.id}
          isOutbound={mine}
          onReply={() => onReply?.()}
          onReact={(emoji) => onReact?.(emoji || null)}
          onClose={() => setShowMenu(false)}
          anchorRef={menuAnchorRef}
          position={mine ? 'right' : 'left'}
          hideReactions={mine || !onReact || heartOnly}
          onCopy={message.content ? () => { void navigator.clipboard?.writeText(message.content).catch(() => undefined); } : undefined}
        />
      )}
    </div>
  );
}
